import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

const links = [
  { to: "/", label: "Home" },
  { to: "/dog-training", label: "Dog Training" },
  { to: "/work-with-toriano", label: "Work With Toriano" },
  { to: "/about", label: "About" },
  { to: "/books-media", label: "Books & Media" },
  { to: "/golden-wolf", label: "Golden Wolf" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <motion.nav
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? "rgba(10,9,8,0.78)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(242,236,225,0.08)" : "none",
        }}
        data-testid="site-nav"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between h-[68px]">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <span className="wolf-mark gold-foil text-2xl">TS</span>
            <span className="text-hair text-bone/70 hidden sm:block">Toriano Sanzone</span>
          </Link>

          <div className="hidden xl:flex items-center gap-7">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-hair transition-colors duration-300 ${loc.pathname === l.to ? "text-gold" : "text-bone/75 hover:text-gold"}`}
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link to="/dog-training" className="btn-gold hidden md:inline-flex" data-testid="nav-cta-book-training">Book Dog Training</Link>
            <Link to="/work-with-toriano" className="btn-outline hidden lg:inline-flex" data-testid="nav-cta-hire">Hire Toriano</Link>
            <button className="xl:hidden text-bone" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="menu">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="17" x2="21" y2="17"/>
              </svg>
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 xl:hidden bg-ink"
            data-testid="mobile-menu"
          >
            <div className="pt-24 pb-32 px-8 flex flex-col gap-5 h-full overflow-y-auto">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`font-display text-4xl md:text-5xl italic ${loc.pathname === l.to ? "text-gold" : "text-bone hover:text-gold"}`}
                    data-testid={`mobile-link-${l.to.replace("/", "") || "home"}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-4">
                <Link to="/dog-training" className="btn-gold self-start" onClick={() => setOpen(false)}>Book Dog Training</Link>
                <Link to="/work-with-toriano" className="btn-outline self-start" onClick={() => setOpen(false)}>Hire Toriano</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
